import type { ShopState, PowerupType, PowerupPurchase } from '@/types/game';
import { getLifeManager } from '@/services/lifeManager';
import { PROPS_CATALOG, type PropEffect } from '@/types/props';

export interface ShopPowerup {
  id: PowerupType;
  name: string;
  description: string;
  cost: number;
  currency: 'coins' | 'gems';
  icon: string;
  maxQuantity: number;
  category: 'consumable' | 'permanent' | 'lives';
  tier: 'basic' | 'premium' | 'legendary';
  unlockLevel?: number;
}

// Storage keys
const STORAGE_KEYS = {
  COINS: 'game_currency_coins',
  GEMS: 'game_currency_gems',
  POWERUPS: 'game_powerups',
  PURCHASES: 'game_purchases',
  UNLOCKED: 'game_unlocked_powerups',
  TOTAL_SPENT: 'game_total_spent',
  DAILY_REWARD: 'game_daily_reward',
  OWNED_PROPS: 'game_owned_props',
  EQUIPPED_PROPS: 'game_equipped_props',
  OWNED_CHARACTERS: 'game_owned_characters',
  EQUIPPED_CHARACTER: 'game_equipped_character',
};

export const SHOP_POWERUPS: Record<PowerupType, ShopPowerup> = {
  shield: {
    id: 'shield',
    name: 'Shield',
    description: 'Protects from one hit',
    cost: 50,
    currency: 'coins',
    icon: 'shield',
    maxQuantity: 5,
    category: 'consumable',
    tier: 'basic',
  },
  invincibility: {
    id: 'invincibility',
    name: 'Invincibility',
    description: 'Immune to damage for 10 seconds',
    cost: 100,
    currency: 'coins',
    icon: 'star',
    maxQuantity: 3,
    category: 'consumable',
    tier: 'premium',
  },
  doubleCoins: {
    id: 'doubleCoins',
    name: 'Double Coins',
    description: '2x coin collection for next level',
    cost: 75,
    currency: 'coins',
    icon: 'coin',
    maxQuantity: 5,
    category: 'consumable',
    tier: 'basic',
  },
  slowTime: {
    id: 'slowTime',
    name: 'Slow Time',
    description: 'Slows down timer by 50%',
    cost: 60,
    currency: 'coins',
    icon: 'clock',
    maxQuantity: 3,
    category: 'consumable',
    tier: 'basic',
  },
  teleport: {
    id: 'teleport',
    name: 'Teleport',
    description: 'Instantly teleport to any valid tile',
    cost: 2,
    currency: 'gems',
    icon: 'portal',
    maxQuantity: 2,
    category: 'consumable',
    tier: 'legendary',
  },
  magnetCoins: {
    id: 'magnetCoins',
    name: 'Coin Magnet',
    description: 'Automatically collect nearby coins',
    cost: 3,
    currency: 'gems',
    icon: 'magnet',
    maxQuantity: 1,
    category: 'permanent',
    tier: 'legendary',
  },
  extraLife: {
    id: 'extraLife',
    name: 'Extra Lives (3)',
    description: 'Get 3 extra lives',
    cost: 50,
    currency: 'coins',
    icon: 'heart',
    maxQuantity: 99,
    category: 'lives',
    tier: 'basic',
  },
  skipLevel: {
    id: 'skipLevel',
    name: 'Skip Level',
    description: 'Skip current level and unlock next',
    cost: 5,
    currency: 'gems',
    icon: 'skip',
    maxQuantity: 3,
    category: 'consumable',
    tier: 'premium',
  },
  hint: {
    id: 'hint',
    name: 'Path Hint',
    description: 'Shows optimal path for 5 seconds',
    cost: 30,
    currency: 'coins',
    icon: 'hint',
    maxQuantity: 5,
    category: 'consumable',
    tier: 'basic',
  },
  undo: {
    id: 'undo',
    name: 'Undo Move',
    description: 'Undo last 3 moves',
    cost: 40,
    currency: 'coins',
    icon: 'undo',
    maxQuantity: 3,
    category: 'consumable',
    tier: 'basic',
  },
};

interface DailyRewardData {
  lastClaim: number;
  streak: number;
  currentDay: number;
}

// Daily rewards configuration - 7-day cycle with escalating rewards
const DAILY_REWARDS = [
  { day: 1, coins: 10, gems: 0 },
  { day: 2, coins: 50, gems: 0 },
  { day: 3, coins: 100, gems: 1 },
  { day: 4, coins: 150, gems: 2 },
  { day: 5, coins: 200, gems: 3, lives: 1 },
  { day: 6, coins: 300, gems: 5 },
  { day: 7, coins: 500, gems: 10, lives: 2, powerups: { 'shield': 1 } },
];

export class ShopManager {
  public coins: number = 0;
  public gems: number = 0;
  public lives: number = 5;
  private lifeManager = getLifeManager();
  private updateCallbacks: Set<() => void> = new Set();
  private dailyRewardData: DailyRewardData = {
    lastClaim: 0,
    streak: 0,
    currentDay: 1,
  };
  
  public powerups: Record<PowerupType, number> = {
    shield: 0,
    invincibility: 0,
    doubleCoins: 0,
    slowTime: 0,
    teleport: 0,
    magnetCoins: 0,
    extraLife: 0,
    skipLevel: 0,
    hint: 0,
    undo: 0,
  };
  public purchases: PowerupPurchase[] = [];
  public unlockedPowerups: Set<PowerupType> = new Set(['shield', 'invincibility']);
  public totalSpent: { coins: number; gems: number } = { coins: 0, gems: 0 };
  public ownedProps: string[] = [];
  public equippedProps: string[] = [];
  public ownedCharacters: string[] = ['default_hero']; // Default character is always owned
  public equippedCharacter: string = 'default_hero';

  constructor() {
    // Load initial state
    this.loadState();
    
    // Load daily reward data
    this.loadDailyRewardData();
    
    // Sync lives with LifeManager
    this.lives = this.lifeManager.getLives();
    
    // Subscribe to life updates
    this.lifeManager.onUpdate((lives) => {
      this.lives = lives;
      this.notifyUpdate();
    });
  }

  private notifyUpdate(): void {
    this.updateCallbacks.forEach(callback => callback());
  }

  public onUpdate(callback: () => void): () => void {
    this.updateCallbacks.add(callback);
    return () => {
      this.updateCallbacks.delete(callback);
    };
  }

  private loadState(): void {
    // Load coins
    const savedCoins = localStorage.getItem(STORAGE_KEYS.COINS);
    if (savedCoins !== null) {
      this.coins = parseInt(savedCoins, 10) || 0;
    }

    // Load gems
    const savedGems = localStorage.getItem(STORAGE_KEYS.GEMS);
    if (savedGems !== null) {
      this.gems = parseInt(savedGems, 10) || 0;
    }

    // Lives are now managed by LifeManager
    // Don't load from localStorage here

    // Load powerups
    const savedPowerups = localStorage.getItem(STORAGE_KEYS.POWERUPS);
    if (savedPowerups) {
      try {
        const parsed = JSON.parse(savedPowerups);
        Object.keys(parsed).forEach(key => {
          if (key in this.powerups) {
            this.powerups[key as PowerupType] = parsed[key] || 0;
          }
        });
      } catch (e) {
        console.error('Failed to parse powerups:', e);
      }
    }

    // Load purchases
    const savedPurchases = localStorage.getItem(STORAGE_KEYS.PURCHASES);
    if (savedPurchases) {
      try {
        this.purchases = JSON.parse(savedPurchases);
      } catch (e) {
        console.error('Failed to parse purchases:', e);
      }
    }

    // Load unlocked powerups
    const savedUnlocked = localStorage.getItem(STORAGE_KEYS.UNLOCKED);
    if (savedUnlocked) {
      try {
        const unlocked = JSON.parse(savedUnlocked);
        this.unlockedPowerups = new Set(unlocked);
      } catch (e) {
        console.error('Failed to parse unlocked powerups:', e);
      }
    }

    // Load total spent
    const savedSpent = localStorage.getItem(STORAGE_KEYS.TOTAL_SPENT);
    if (savedSpent) {
      try {
        this.totalSpent = JSON.parse(savedSpent);
      } catch (e) {
        console.error('Failed to parse total spent:', e);
      }
    }
    
    // Load owned props
    this.loadOwnedProps();
    
    // Load equipped props
    this.loadEquippedProps();
    
    // Load owned characters
    this.loadOwnedCharacters();
    
    // Load equipped character
    this.loadEquippedCharacter();
  }

  private saveCoins(): void {
    localStorage.setItem(STORAGE_KEYS.COINS, this.coins.toString());
  }

  private saveGems(): void {
    localStorage.setItem(STORAGE_KEYS.GEMS, this.gems.toString());
  }

  private savePowerups(): void {
    localStorage.setItem(STORAGE_KEYS.POWERUPS, JSON.stringify(this.powerups));
  }

  private savePurchases(): void {
    localStorage.setItem(STORAGE_KEYS.PURCHASES, JSON.stringify(this.purchases));
  }

  private saveUnlocked(): void {
    localStorage.setItem(STORAGE_KEYS.UNLOCKED, JSON.stringify(Array.from(this.unlockedPowerups)));
  }

  private saveTotalSpent(): void {
    localStorage.setItem(STORAGE_KEYS.TOTAL_SPENT, JSON.stringify(this.totalSpent));
  }

  // Currency methods
  public addCoins(amount: number): void {
    this.coins += amount;
    this.saveCoins();
    this.notifyUpdate();
  }

  public addGems(amount: number): void {
    this.gems += amount;
    this.saveGems();
    this.notifyUpdate();
  }

  public spendCoins(amount: number): boolean {
    if (this.coins >= amount) {
      this.coins -= amount;
      this.totalSpent.coins += amount;
      this.saveCoins();
      this.saveTotalSpent();
      this.notifyUpdate();
      return true;
    }
    return false;
  }

  public spendGems(amount: number): boolean {
    if (this.gems >= amount) {
      this.gems -= amount;
      this.totalSpent.gems += amount;
      this.saveGems();
      this.saveTotalSpent();
      this.notifyUpdate();
      return true;
    }
    return false;
  }

  // Life methods (delegate to LifeManager)
  public setLives(lives: number): void {
    this.lifeManager.setLives(lives);
    this.lives = this.lifeManager.getLives();
  }

  public loseLife(): boolean {
    const hasLivesLeft = this.lifeManager.loseLife();
    this.lives = this.lifeManager.getLives();
    return hasLivesLeft;
  }

  public addLives(amount: number): void {
    this.lifeManager.addLives(amount);
    this.lives = this.lifeManager.getLives();
  }

  public getTimeUntilNextLife(): number {
    return this.lifeManager.getTimeUntilNextLife();
  }

  public getFormattedTimeUntilNextLife(): string {
    return this.lifeManager.getFormattedTimeUntilNextLife();
  }

  // Powerup methods
  public addPowerup(type: PowerupType, amount: number = 1): void {
    this.powerups[type] = Math.min(
      this.powerups[type] + amount,
      SHOP_POWERUPS[type].maxQuantity
    );
    this.savePowerups();
    this.notifyUpdate();
  }

  public usePowerup(type: PowerupType): boolean {
    if (this.powerups[type] > 0) {
      this.powerups[type]--;
      this.savePowerups();
      this.notifyUpdate();
      return true;
    }
    return false;
  }

  public hasPowerup(type: PowerupType): boolean {
    return this.powerups[type] > 0;
  }

  // Purchase methods
  public purchasePowerup(type: PowerupType): boolean {
    const powerup = SHOP_POWERUPS[type];
    if (!powerup) return false;

    // Check if already at max quantity
    if (this.powerups[type] >= powerup.maxQuantity) {
      return false;
    }

    // Check currency and spend
    const canAfford = powerup.currency === 'coins' 
      ? this.spendCoins(powerup.cost)
      : this.spendGems(powerup.cost);

    if (canAfford) {
      // Special handling for extra lives
      if (type === 'extraLife') {
        this.addLives(3);
      } else {
        this.addPowerup(type, 1);
      }

      // Record purchase
      this.purchases.push({
        powerupId: type,
        timestamp: Date.now(),
        cost: powerup.cost,
        currency: powerup.currency,
      });
      this.savePurchases();

      // Unlock powerup if not already unlocked
      if (!this.unlockedPowerups.has(type)) {
        this.unlockedPowerups.add(type);
        this.saveUnlocked();
      }

      return true;
    }

    return false;
  }

  public unlockPowerup(type: PowerupType): void {
    this.unlockedPowerups.add(type);
    this.saveUnlocked();
    this.notifyUpdate();
  }

  public isPowerupUnlocked(type: PowerupType): boolean {
    return this.unlockedPowerups.has(type);
  }

  public getMaxLives(): number {
    return this.lifeManager.getMaxLives();
  }

  // Get shop state
  public getShopState(): ShopState {
    // Always get fresh lives from LifeManager
    this.lives = this.lifeManager.getLives();
    return {
      coins: this.coins,
      gems: this.gems,
      lives: this.lives,
      powerups: { ...this.powerups },
      purchases: [...this.purchases],
      unlockedPowerups: new Set(this.unlockedPowerups),
      totalSpent: { ...this.totalSpent },
      ownedProps: [...this.ownedProps],
      equippedProps: [...this.equippedProps],
      ownedCharacters: [...this.ownedCharacters],
      equippedCharacter: this.equippedCharacter,
    };
  }

  // Daily reward methods
  private loadDailyRewardData(): void {
    const saved = localStorage.getItem(STORAGE_KEYS.DAILY_REWARD);
    if (saved) {
      try {
        this.dailyRewardData = JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse daily reward data:', e);
      }
    }
  }

  private saveDailyRewardData(): void {
    localStorage.setItem(STORAGE_KEYS.DAILY_REWARD, JSON.stringify(this.dailyRewardData));
  }

  public canClaimDailyReward(): boolean {
    const now = Date.now();
    const lastClaim = this.dailyRewardData.lastClaim;
    
    if (!lastClaim) return true; // Never claimed before
    
    // Check if it's a new day
    const lastClaimDate = new Date(lastClaim);
    const currentDate = new Date(now);
    
    // Reset to start of day for comparison
    lastClaimDate.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    
    return currentDate.getTime() > lastClaimDate.getTime();
  }

  public getDailyRewardStatus(): { canClaim: boolean; currentDay: number; streak: number } {
    const canClaim = this.canClaimDailyReward();
    const now = Date.now();
    const lastClaim = this.dailyRewardData.lastClaim;
    
    if (!lastClaim) {
      return { canClaim: true, currentDay: 1, streak: 0 };
    }
    
    // Check if streak is broken (missed a day)
    const daysSinceLastClaim = Math.floor((now - lastClaim) / (1000 * 60 * 60 * 24));
    
    if (daysSinceLastClaim > 1) {
      // Streak broken, reset
      this.dailyRewardData.streak = 0;
      this.dailyRewardData.currentDay = 1;
      this.saveDailyRewardData();
    }
    
    return {
      canClaim,
      currentDay: this.dailyRewardData.currentDay,
      streak: this.dailyRewardData.streak,
    };
  }

  public claimDailyReward(): boolean {
    if (!this.canClaimDailyReward()) return false;
    
    const now = Date.now();
    const daysSinceLastClaim = this.dailyRewardData.lastClaim ? 
      Math.floor((now - this.dailyRewardData.lastClaim) / (1000 * 60 * 60 * 24)) : 999;
    
    // Update streak
    if (daysSinceLastClaim === 1 || !this.dailyRewardData.lastClaim) {
      // Consecutive day or first claim
      this.dailyRewardData.streak++;
    } else if (daysSinceLastClaim > 1) {
      // Missed a day, reset streak
      this.dailyRewardData.streak = 1;
      this.dailyRewardData.currentDay = 1;
    }
    
    // Get current reward day (1-7, cycling)
    const rewardDay = ((this.dailyRewardData.streak - 1) % 7) + 1;
    this.dailyRewardData.currentDay = rewardDay;
    
    // Get reward
    const reward = DAILY_REWARDS[rewardDay - 1];
    
    // Grant rewards
    if (reward.coins > 0) {
      this.addCoins(reward.coins);
    }
    if (reward.gems > 0) {
      this.addGems(reward.gems);
    }
    if (reward.lives) {
      this.addLives(reward.lives);
    }
    if (reward.powerups) {
      Object.entries(reward.powerups).forEach(([type, amount]) => {
        this.addPowerup(type as PowerupType, amount);
      });
    }
    
    // Update last claim time
    this.dailyRewardData.lastClaim = now;
    this.saveDailyRewardData();
    this.notifyUpdate();
    
    return true;
  }

  // Testing/debug methods
  public addTestCurrency(): void {
    this.addCoins(1000);
    this.addGems(50);
  }

  public resetProgress(): void {
    this.coins = 100;
    this.gems = 10;
    this.powerups = {
      shield: 0,
      invincibility: 0,
      doubleCoins: 0,
      slowTime: 0,
      teleport: 0,
      magnetCoins: 0,
      extraLife: 0,
      skipLevel: 0,
      hint: 0,
      undo: 0,
    };
    this.purchases = [];
    this.unlockedPowerups = new Set(['shield', 'invincibility']);
    this.totalSpent = { coins: 0, gems: 0 };
    
    // Reset daily rewards
    this.dailyRewardData = {
      lastClaim: 0,
      streak: 0,
      currentDay: 1,
    };
    
    // Reset lives through LifeManager (start with 5 lives)
    this.lifeManager.setLives(5);
    this.lives = this.lifeManager.getLives();

    // Save all
    this.saveCoins();
    this.saveGems();
    this.savePowerups();
    this.savePurchases();
    this.saveUnlocked();
    this.saveTotalSpent();
    this.saveDailyRewardData();
    this.notifyUpdate();
  }

  // Prop management methods
  public purchaseProp(propId: string): boolean {
    const prop = PROPS_CATALOG.find(p => p.id === propId);
    if (!prop) return false;
    
    // Check if already owned
    if (this.ownedProps.includes(propId)) return false;
    
    // Check currency
    if (prop.price.coins && this.coins < prop.price.coins) return false;
    if (prop.price.gems && this.gems < prop.price.gems) return false;
    
    // Deduct currency
    if (prop.price.coins) {
      this.spendCoins(prop.price.coins);
    }
    if (prop.price.gems) {
      this.spendGems(prop.price.gems);
    }
    
    // Add to owned props
    this.ownedProps.push(propId);
    this.saveOwnedProps();
    this.notifyUpdate();
    
    return true;
  }
  
  public equipProp(propId: string): boolean {
    // Check if owned
    if (!this.ownedProps.includes(propId)) return false;
    
    const prop = PROPS_CATALOG.find(p => p.id === propId);
    if (!prop) return false;
    
    // Unequip any prop in the same slot
    const existingPropIndex = this.equippedProps.findIndex(id => {
      const existingProp = PROPS_CATALOG.find(p => p.id === id);
      return existingProp?.slot === prop.slot;
    });
    
    if (existingPropIndex !== -1) {
      this.equippedProps.splice(existingPropIndex, 1);
    }
    
    // Equip the new prop
    this.equippedProps.push(propId);
    this.saveEquippedProps();
    this.notifyUpdate();
    
    return true;
  }
  
  public unequipProp(propId: string): boolean {
    const index = this.equippedProps.indexOf(propId);
    if (index === -1) return false;
    
    this.equippedProps.splice(index, 1);
    this.saveEquippedProps();
    this.notifyUpdate();
    
    return true;
  }
  
  public getEquippedPropEffects(): PropEffect[] {
    const effects: PropEffect[] = [];
    
    for (const propId of this.equippedProps) {
      const prop = PROPS_CATALOG.find(p => p.id === propId);
      if (prop) {
        effects.push(...prop.effects);
      }
    }
    
    return effects;
  }
  
  private saveOwnedProps(): void {
    localStorage.setItem(STORAGE_KEYS.OWNED_PROPS, JSON.stringify(this.ownedProps));
  }
  
  private saveEquippedProps(): void {
    localStorage.setItem(STORAGE_KEYS.EQUIPPED_PROPS, JSON.stringify(this.equippedProps));
  }
  
  private loadOwnedProps(): void {
    const saved = localStorage.getItem(STORAGE_KEYS.OWNED_PROPS);
    if (saved) {
      try {
        this.ownedProps = JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse owned props:', e);
      }
    }
  }
  
  private loadEquippedProps(): void {
    const saved = localStorage.getItem(STORAGE_KEYS.EQUIPPED_PROPS);
    if (saved) {
      try {
        this.equippedProps = JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse equipped props:', e);
      }
    }
  }
  
  // Character management methods
  public purchaseCharacter(characterId: string, price: number, currency: 'coins' | 'gems'): boolean {
    // Check if already owned
    if (this.ownedCharacters.includes(characterId)) {
      return false;
    }
    
    // Check if player can afford it
    const balance = currency === 'coins' ? this.coins : this.gems;
    if (balance < price) {
      return false;
    }
    
    // Deduct currency
    if (currency === 'coins') {
      this.coins -= price;
      this.saveCoins();
    } else {
      this.gems -= price;
      this.saveGems();
    }
    
    // Add to owned characters
    this.ownedCharacters.push(characterId);
    this.saveOwnedCharacters();
    
    // Track spending
    this.totalSpent[currency] += price;
    this.saveTotalSpent();
    
    this.notifyUpdate();
    return true;
  }
  
  public equipCharacter(characterId: string): boolean {
    if (!this.ownedCharacters.includes(characterId)) {
      return false;
    }
    
    this.equippedCharacter = characterId;
    this.saveEquippedCharacter();
    this.notifyUpdate();
    
    return true;
  }
  
  private saveOwnedCharacters(): void {
    localStorage.setItem(STORAGE_KEYS.OWNED_CHARACTERS, JSON.stringify(this.ownedCharacters));
  }
  
  private saveEquippedCharacter(): void {
    localStorage.setItem(STORAGE_KEYS.EQUIPPED_CHARACTER, this.equippedCharacter);
  }
  
  private loadOwnedCharacters(): void {
    const saved = localStorage.getItem(STORAGE_KEYS.OWNED_CHARACTERS);
    if (saved) {
      try {
        this.ownedCharacters = JSON.parse(saved);
        // Ensure default character is always owned
        if (!this.ownedCharacters.includes('default_hero')) {
          this.ownedCharacters.push('default_hero');
        }
      } catch (e) {
        console.error('Failed to parse owned characters:', e);
        this.ownedCharacters = ['default_hero'];
      }
    }
  }
  
  private loadEquippedCharacter(): void {
    const saved = localStorage.getItem(STORAGE_KEYS.EQUIPPED_CHARACTER);
    if (saved && this.ownedCharacters.includes(saved)) {
      this.equippedCharacter = saved;
    } else {
      this.equippedCharacter = 'default_hero';
    }
  }
}
